import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
    X, Edit2, Trash2, Plus, AlertTriangle,
    CalendarDays,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    RefreshCw,
    ArrowUpDown,
    Search,
    Download,
    CircleEllipsis,
    Eye
} from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError, showSuccess } from "../../../utils/toast"; // Adjust import path
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown "; // Adjust import path
import AddScheduleModal from "./AddSchedule"; // Import the modal
import { useClientLevel } from '../../../hooks/useClientLevel';
import { hasPermission } from "../../../utils/permissionsHelper";
import AgentExportModal from "../../Agent/AgentExportModal";
import ColumnVisibilityDropdown, { type ColumnOption } from "../../../components/ColumnVisibilityDropdown";

interface Schedule {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    name: string;
    is_enabled: boolean;
    organization_id: number;
    client_id: number;
    task_type: string;
    is_global: boolean;
    scheduler_data: string;
    data: string;
}

export type ScheduleColumnKey =
    | 'name'
    | 'task_type'
    | 'is_global'
    | 'is_enabled'
    | 'created'
    | 'actions';

export default function Schedules() {
    const [schedules, setSchedules] = useState<Schedule[]>([]);
    const [loading, setLoading] = useState(true);

    // Panel state
    const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);

    // Add/Edit Modal state
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [scheduleToEdit, setScheduleToEdit] = useState<Schedule | null>(null);

    // Delete Modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [scheduleToDelete, setScheduleToDelete] = useState<Schedule | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Actions dropdown menu state
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: string | number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition({ top: rect.bottom + 4, left: Math.max(10, rect.left - 110) });
        setOpenMenuId(prev => (prev === String(id) ? null : String(id)));
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (openMenuId && !target.closest(`[data-menu-id="${openMenuId}"]`) && !target.closest('[data-action-menu]')) {
                setOpenMenuId(null);
            }
        };
        const handleScroll = () => {
            if (openMenuId) setOpenMenuId(null);
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [openMenuId]);

    // Pagination, Sorting & Search state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);
    const [orderBy, setOrderBy] = useState<string>('created');
    const [orderDir, setOrderDir] = useState<'ASC' | 'DESC'>('DESC');

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const level = useClientLevel();
    const canCreate = hasPermission('scheduler', 'create');
    const canUpdate = hasPermission('scheduler', 'update');
    const canDelete = hasPermission('scheduler', 'delete');

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState<Record<ScheduleColumnKey, boolean>>({
        name: true,
        task_type: true,
        is_global: true,
        is_enabled: true,
        created: true,
        actions: true,
    });

    const columnOptions: ColumnOption<ScheduleColumnKey>[] = useMemo(() => [
        { key: 'name', label: 'Name' },
        { key: 'task_type', label: 'Task Type' },
        { key: 'is_global', label: 'Scope' },
        { key: 'is_enabled', label: 'Status' },
        { key: 'created', label: 'Created' },
        ...((canUpdate || canDelete) ? [{ key: 'actions' as ScheduleColumnKey, label: 'Actions' }] : []),
    ], [canUpdate, canDelete]);

    const visibleColsCount = useMemo(() => {
        let count = 0;
        if (visibleColumns.name) count++;
        if (visibleColumns.task_type) count++;
        if (visibleColumns.is_global) count++;
        if (visibleColumns.is_enabled) count++;
        if (visibleColumns.created) count++;
        if ((canUpdate || canDelete) && visibleColumns.actions) count++;
        return count || 1;
    }, [visibleColumns, canUpdate, canDelete]);


    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500); // 500ms delay
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchSchedules = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;
            const queryObj: any = {}


            if (level.id) {
                queryObj.is_global = false;
                // You can also add client_id if the backend requires it, but is_global: false might be enough if the backend already filters by client_id automatically, but let's send both to be safe
                queryObj.client_id = level.id;
            } else {
                queryObj.is_global = true;
            }


            if (debouncedSearch) {
                queryObj.name = { $ilike: `%${debouncedSearch}%` };
            }

            const params: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj),
                sort_by: orderBy,
                sort_order: orderDir
            };



            const response = await baseapi.get("/scheduler", { params });

            if (response.data.status) {
                setSchedules(response.data.data);
                setTotalRecords(response.data.total || response.data.data.length);
            } else {
                showError(response.data.message || "Failed to load schedules");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchSchedules();
    }, [level, currentPage, limit, orderBy, orderDir, debouncedSearch]);

    // Handle Sort
    const handleSort = (field: string) => {
        if (orderBy === field) {
            setOrderDir(orderDir === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setOrderBy(field);
            setOrderDir('DESC');
        }
        setCurrentPage(1);
    };

    // Handle Delete
    const handleDeleteConfirm = async () => {
        if (!scheduleToDelete) return;
        setIsDeleting(true);
        try {
            const response = await baseapi.delete(`/scheduler/${scheduleToDelete.id}`);
            if (response.data.status) {
                showSuccess("Schedule deleted successfully");
                setIsDeleteModalOpen(false);
                setScheduleToDelete(null);
                fetchSchedules();
            } else {
                showError(response.data.message || "Failed to delete schedule");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setIsDeleting(false);
        }
    };

    // Helper to format string values safely
    const formatValue = (value: any): string => {
        if (value === null || value === undefined || value === '') return "-";
        if (typeof value === "boolean") return value ? "true" : "false";
        if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "-";
        if (typeof value === "object") return JSON.stringify(value, null, 2);
        return String(value).replace(/_/g, " ");
    };

    // Helper to parse and render dynamic JSON strings
    const renderDynamicData = (jsonString: string | undefined | null) => {
        if (!jsonString) return <span className="text-gray-500 dark:text-gray-400 text-xs">No data available</span>;

        try {
            const parsedData = typeof jsonString === 'object' ? jsonString : JSON.parse(jsonString);
            if (!parsedData || typeof parsedData !== 'object') {
                return <span className="text-xs text-gray-800 dark:text-gray-200 font-mono">{String(parsedData)}</span>;
            }
            const keys = Object.keys(parsedData);

            if (keys.length === 0) return <span className="text-gray-500 dark:text-gray-400 text-xs">Empty</span>;

            return (
                <div className="flex flex-col space-y-3">
                    {keys.map((key) => (
                        <div key={key} className="flex flex-col border-b border-gray-200 dark:border-gray-700/60 pb-2 last:border-0">
                            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-0.5">
                                {key.replace(/_/g, " ")}
                            </span>
                            <span className="text-xs text-gray-800 dark:text-gray-200 font-mono break-words whitespace-pre-wrap">
                                {formatValue(parsedData[key])}
                            </span>
                        </div>
                    ))}
                </div>
            );
        } catch (e) {
            return <span className="text-red-500 dark:text-red-400 text-xs">Invalid data format</span>;
        }
    };

    return (
        <div className="animate-in fade-in duration-300 w-full flex flex-col bg-transparent text-gray-900 dark:text-gray-200">
            {/* Action Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2 shrink-0 py-1 sm:py-2">
                <div className="flex items-center gap-1.5 shrink-0">
                    <div className="bg-orange-600 p-1.5 rounded shadow-lg shrink-0">
                        <CalendarDays className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h1 className="font-bold text-base sm:text-lg text-gray-900 dark:text-gray-200 leading-none">Scheduler</h1>
                    </div>
                    {!loading && totalRecords !== undefined && (
                        <div className="font-bold text-base sm:text-lg text-gray-600 dark:text-gray-100">
                            ({totalRecords})
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 w-full sm:w-auto">
                    <button
                        onClick={() => fetchSchedules()}
                        className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer shrink-0"
                        title="Refresh"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </button>

                    <div className="relative flex-1 sm:w-56 min-w-[130px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                        <input
                            type="text"
                            placeholder="Search schedules..."
                            className="w-full bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 focus:border-orange-500 focus:bg-white dark:focus:bg-gray-800 outline-none transition-all"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>

                    <div className="shrink-0">
                        <ColumnVisibilityDropdown
                            columns={columnOptions}
                            visibleColumns={visibleColumns}
                            onChange={setVisibleColumns}
                        />
                    </div>

                    <button
                        onClick={() => setShowExportModal(true)}
                        className="flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-[11px] text-gray-700 dark:text-white font-bold hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer shrink-0 whitespace-nowrap"
                    >
                        <Download className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                        <span>Export</span>
                    </button>

                    {canCreate && (
                        <button
                            onClick={() => {
                                setScheduleToEdit(null);
                                setIsAddModalOpen(true);
                            }}
                            className="flex items-center gap-1.5 sm:gap-2 bg-orange-600 hover:bg-orange-500 text-white text-[11px] font-bold px-3 py-1.5 rounded-lg transition-all shadow-lg shadow-orange-900/20 cursor-pointer shrink-0 whitespace-nowrap"
                        >
                            <Plus size={14} />
                            <span>Add Schedule</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Table Area */}
            <div className="flex flex-col">
                <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl flex flex-col">
                    <div className="relative overflow-x-auto custom-scroll">
                        <table className="w-full text-left border-collapse table-fixed">
                            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-200 dark:border-gray-700/50">
                                    {visibleColumns.name && (
                                        <th
                                            className="w-2/6 px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest truncate cursor-pointer group hover:text-gray-900 dark:hover:text-white transition-colors"
                                            onClick={() => handleSort('name')}
                                        >
                                            <div className="flex items-center gap-2">
                                                
                                                <span className="truncate">Name</span>
                                                <ArrowUpDown className={`w-3 h-3 ${orderBy === 'name' ? 'text-orange-500' : 'text-gray-400 dark:text-gray-600 group-hover:text-gray-600 dark:group-hover:text-gray-400'}`} />
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.task_type && (
                                        <th
                                            className="w-1/6 px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest truncate cursor-pointer group hover:text-gray-900 dark:hover:text-white transition-colors"
                                            onClick={() => handleSort('task_type')}
                                        >
                                            <div className="flex items-center gap-2">
                                             
                                                <span className="truncate">Task Type</span>
                                                <ArrowUpDown className={`w-3 h-3 ${orderBy === 'task_type' ? 'text-orange-500' : 'text-gray-400 dark:text-gray-600 group-hover:text-gray-600 dark:group-hover:text-gray-400'}`} />
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.is_global && (
                                        <th
                                            className="w-1/6 px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center truncate cursor-pointer group hover:text-gray-900 dark:hover:text-white transition-colors"
                                            onClick={() => handleSort('is_global')}
                                        >
                                            <div className="flex items-center justify-center gap-2">
                                             
                                                <span className="truncate">Scope</span>
                                                <ArrowUpDown className={`w-3 h-3 ${orderBy === 'is_global' ? 'text-orange-500' : 'text-gray-400 dark:text-gray-600 group-hover:text-gray-600 dark:group-hover:text-gray-400'}`} />
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.is_enabled && (
                                        <th
                                            className="w-1/6 px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center truncate cursor-pointer group hover:text-gray-900 dark:hover:text-white transition-colors"
                                            onClick={() => handleSort('is_enabled')}
                                        >
                                            <div className="flex items-center justify-center gap-2">
                                             
                                                <span className="truncate">Status</span>
                                                <ArrowUpDown className={`w-3 h-3 ${orderBy === 'is_enabled' ? 'text-orange-500' : 'text-gray-400 dark:text-gray-600 group-hover:text-gray-600 dark:group-hover:text-gray-400'}`} />
                                            </div>
                                        </th>
                                    )}
                                    {visibleColumns.created && (
                                        <th
                                            className="w-1/6 px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center truncate cursor-pointer group hover:text-gray-900 dark:hover:text-white transition-colors"
                                            onClick={() => handleSort('created')}
                                        >
                                            <div className="flex items-center justify-center gap-2">
                                               
                                                <span className="truncate">Created</span>
                                                <ArrowUpDown className={`w-3 h-3 ${orderBy === 'created' ? 'text-orange-500' : 'text-gray-400 dark:text-gray-600 group-hover:text-gray-600 dark:group-hover:text-gray-400'}`} />
                                            </div>
                                        </th>
                                    )}

                                    {(canUpdate || canDelete) && visibleColumns.actions && (
                                        <th className="w-1/6 px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center truncate">
                                            <div className="flex items-center justify-center gap-2">
                                                 <span className="truncate">Actions</span>
                                            </div>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700/60 bg-white dark:bg-gray-900/20 text-xs">
                                {loading && schedules.length === 0 ? (
                                    <tr>
                                        <td colSpan={visibleColsCount} className="py-12 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-2">
                                                <div className="w-6 h-6 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                                                <p className="text-[9px] font-black text-gray-500 uppercase tracking-[0.2em]">Fetching Schedules...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : schedules.length === 0 ? (
                                    <tr>
                                        <td colSpan={visibleColsCount} className="py-12 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-2 opacity-50">
                                                <CalendarDays size={24} className="text-gray-400" />
                                                <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">
                                                    {debouncedSearch ? "No matching schedules found" : "No schedules found"}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    schedules.map((schedule) => (
                                        <tr key={schedule.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors group">
                                            {visibleColumns.name && (
                                                <td className="px-4 py-1 truncate">
                                                    <button
                                                        onClick={() => setSelectedSchedule(schedule)}
                                                        className="text-[12px] font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:underline tracking-wide truncate focus:outline-none text-left w-full cursor-pointer"
                                                    >
                                                        {schedule.name}
                                                    </button>
                                                </td>
                                            )}
                                            {visibleColumns.task_type && (
                                                <td className="px-4 py-1 truncate">
                                                    <span className="px-1.5 py-0.5 bg-indigo-50 dark:bg-gray-800 border border-indigo-200 dark:border-gray-700 rounded text-[9px] font-bold text-indigo-700 dark:text-indigo-400 uppercase tracking-wider">
                                                        {schedule.task_type.replace(/_/g, " ")}
                                                    </span>
                                                </td>
                                            )}
                                            {visibleColumns.is_global && (
                                                <td className="px-4 py-1 text-center truncate">
                                                    {schedule.is_global ? (
                                                        <span className="text-[10px] font-bold uppercase text-purple-600 dark:text-purple-400">Global</span>
                                                    ) : (
                                                        <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400">Local</span>
                                                    )}
                                                </td>
                                            )}
                                            {visibleColumns.is_enabled && (
                                                <td className="px-4 py-1 text-center truncate">
                                                    {schedule.is_enabled ? (
                                                        <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-500/20">
                                                             Enabled
                                                        </span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/20">
                                                             Disabled
                                                        </span>
                                                    )}
                                                </td>
                                            )}
                                            {visibleColumns.created && (
                                                <td className="px-4 py-1 text-center truncate">
                                                    <span className="text-[10px] font-bold uppercase text-gray-700 dark:text-gray-200">{schedule.created ? new Date(schedule.created + "Z").toLocaleString() : "-"}</span>
                                                </td>
                                            )}

                                            {(canUpdate || canDelete) && visibleColumns.actions && (
                                                <td className="px-4 py-1 text-center truncate">
                                                    <button
                                                        data-menu-id={schedule.id}
                                                        onClick={(e) => handleMenuOpen(e, schedule.id)}
                                                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
                                                        title="Actions"
                                                    >
                                                        <CircleEllipsis className="w-4.5 h-4.5" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50/80 dark:bg-gray-800/40 border-t border-gray-200 dark:border-gray-700 shrink-0">
                        <div className="flex items-center gap-3">
                            <ItemsPerPageDropdown
                                value={limit}
                                onChange={(v) => {
                                    setLimit(v);
                                    setCurrentPage(1);
                                }}
                            />
                            <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                                <span className="text-gray-700 dark:text-gray-300">
                                    {totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1}
                                </span>
                                {" - "}
                                <span className="text-gray-700 dark:text-gray-300">
                                    {Math.min(currentPage * limit, totalRecords)}
                                </span>
                                <span className="mx-1 text-gray-400 dark:text-gray-600">/</span>
                                <span className="text-gray-900 dark:text-gray-300 font-bold">{totalRecords}</span>
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(1)}
                                className="p-1.5 text-gray-500 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"
                            >
                                <ChevronsLeft size={14} />
                            </button>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                className="p-1.5 text-gray-500 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"
                            >
                                <ChevronLeft size={14} />
                            </button>
                            <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">
                                {currentPage}
                            </div>
                            <button
                                disabled={currentPage * limit >= totalRecords}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="p-1.5 text-gray-500 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"
                            >
                                <ChevronRight size={14} />
                            </button>
                            <button
                                disabled={currentPage * limit >= totalRecords}
                                onClick={() => setCurrentPage(Math.ceil(totalRecords / limit))}
                                className="p-1.5 text-gray-500 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 cursor-pointer"
                            >
                                <ChevronsRight size={14} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side Drawer for Viewing Details */}
            {selectedSchedule && (
                <>
                    <div className="fixed inset-0 bg-black/40 z-40 transition-opacity" onClick={() => setSelectedSchedule(null)} />
                    <div className="fixed top-0 right-0 h-full w-96 max-w-[90vw] bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl z-55 transform transition-transform duration-300 flex flex-col">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/50">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide truncate pr-4">
                                {selectedSchedule.name}
                            </h2>
                            <button
                                onClick={() => setSelectedSchedule(null)}
                                className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded transition-colors cursor-pointer"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5 custom-scroll space-y-6">
                            <div className="bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50 rounded-lg p-4">
                                <h3 className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-4 pb-2 border-b border-gray-200 dark:border-gray-700/50">
                                    Scheduler Configuration
                                </h3>
                                {renderDynamicData(selectedSchedule.scheduler_data)}
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50 rounded-lg p-4">
                                <h3 className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-4 pb-2 border-b border-gray-200 dark:border-gray-700/50">
                                    Task Parameters
                                </h3>
                                {renderDynamicData(selectedSchedule.data)}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Add / Edit Modal */}
            <AddScheduleModal
                isOpen={isAddModalOpen}
                onClose={() => {
                    setIsAddModalOpen(false);
                    setScheduleToEdit(null);
                }}
                onSuccess={fetchSchedules}
                editData={scheduleToEdit}

            />

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="fixed inset-0 bg-black/50 dark:bg-black/60 backdrop-blur-sm" onClick={() => !isDeleting && setIsDeleteModalOpen(false)} />
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl w-full max-w-sm relative flex flex-col overflow-hidden zoom-in-95">
                        <div className="p-6">
                            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-500/10 flex items-center justify-center mb-4 mx-auto">
                                <AlertTriangle className="w-6 h-6 text-red-500" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white text-center mb-2">Delete Schedule</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                                Are you sure you want to delete <span className="text-gray-900 dark:text-gray-200 font-semibold">{scheduleToDelete?.name}</span>? This action cannot be undone.
                            </p>
                        </div>
                        <div className="flex bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-800">
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                disabled={isDeleting}
                                className="flex-1 py-3 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 cursor-pointer"
                            >
                                Cancel
                            </button>
                            <div className="w-px bg-gray-200 dark:bg-gray-800"></div>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={isDeleting}
                                className="flex-1 py-3 text-sm font-bold text-red-600 dark:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                            >
                                {isDeleting ? (
                                    <div className="w-4 h-4 border-2 border-red-500/20 border-t-red-500 rounded-full animate-spin" />
                                ) : (
                                    "Delete"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <AgentExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                templateName="schedulers"
                clientId={level?.id}
            />

            {/* Actions Portal Menu */}
            {openMenuId && createPortal(
                (() => {
                    const activeSchedule = schedules.find(s => String(s.id) === String(openMenuId));
                    if (!activeSchedule) return null;
                    return (
                        <div
                            data-action-menu
                            className="fixed w-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[9999] overflow-hidden py-1.5 animate-in fade-in zoom-in-95 duration-100"
                            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                        >
                            <button
                                onClick={() => {
                                    setSelectedSchedule(activeSchedule);
                                    setOpenMenuId(null);
                                }}
                                className="w-full text-left px-4 py-2.5 text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors cursor-pointer"
                            >
                                <Eye className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" /> View Details
                            </button>
                            {canUpdate && (
                                <button
                                    onClick={() => {
                                        setScheduleToEdit(activeSchedule);
                                        setIsAddModalOpen(true);
                                        setOpenMenuId(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors cursor-pointer"
                                >
                                    <Edit2 className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /> Edit Schedule
                                </button>
                            )}
                            {canDelete && (
                                <button
                                    onClick={() => {
                                        setScheduleToDelete(activeSchedule);
                                        setIsDeleteModalOpen(true);
                                        setOpenMenuId(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-[11px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-3 font-bold cursor-pointer"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Delete Schedule
                                </button>
                            )}
                        </div>
                    );
                })(),
                document.body
            )}
        </div>
    );
}